import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Switch } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { apiFetch } from "../../utils/apiFetch";
import DealHistory from "./DealHistory";
import { formatDate, formatDateTime } from "./dealFormat";
import { MARKETING_STATUS_CLASS, MARKETING_STATUS_LABELS } from "./marketingStatus";
import "./global/global.css";

const LIST_PATH = "/marketing";

const Field = ({ label, children }) => (
  <div className="deal-details-field">
    <span className="deal-details-label">{label}</span>
    <span className="deal-details-value">{children}</span>
  </div>
);

const Flyers = ({ flyers }) => {
  const labels = [...new Set(flyers.map((flyer) => flyer.label))];
  if (labels.length === 0) {
    return <p className="deal-details-empty">No flyers yet.</p>;
  }
  return (
    <div className="marketing-flyers">
      {labels.map((label) => {
        const jpeg = flyers.find((f) => f.label === label && f.format === "jpeg");
        const pdf = flyers.find((f) => f.label === label && f.format === "pdf");
        return (
          <div key={label} className="marketing-flyer">
            {jpeg && (
              <a href={jpeg.url} target="_blank" rel="noopener noreferrer">
                <img src={jpeg.url} alt={`${label} flyer`} className="marketing-flyer-img" />
              </a>
            )}
            <small>{label}</small>
            {pdf && (
              <>
                {" "}
                <a href={pdf.url} target="_blank" rel="noopener noreferrer">
                  PDF
                </a>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const PrintOrders = ({ orders }) => {
  if (orders.length === 0) {
    return <p className="deal-details-empty">No print orders yet.</p>;
  }
  return (
    <div className="marketing-flyers">
      {orders.map((order) => (
        <div key={order.id} className="marketing-flyer">
          {order.preview_url ? (
            <a href={order.preview_url} target="_blank" rel="noopener noreferrer">
              <img
                src={order.preview_url}
                alt={`${order.layout} print preview`}
                className="marketing-flyer-img"
              />
            </a>
          ) : (
            <small>No preview yet</small>
          )}
          <div className="deal-details-grid">
            <Field label="Status">{order.status}</Field>
            <Field label="Flyer">
              {order.quantity} × {order.layout}
            </Field>
            <Field label="Ship to">{order.address}</Field>
            <Field label="Placed">{formatDateTime(order.placed_at)}</Field>
            {order.tracking_url && (
              <Field label="Tracking">
                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                  Track package
                </a>
              </Field>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const PropertySite = ({ page, onToggle, toggleError }) => {
  if (!page) {
    return <p className="deal-details-empty">No property site yet.</p>;
  }
  return (
    <>
      {toggleError && <Alert severity="error">{toggleError}</Alert>}
      <div className="deal-details-grid">
        <Field label="Site">
          <a
            href={`${process.env.REACT_APP_PAYVIEW_BASE_URL}${page.url}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View site
          </a>
        </Field>
        <Field label="Created">{formatDate(page.created_at)}</Field>
        <Field label="Live">
          <Switch
            checked={page.is_active}
            onChange={onToggle}
            color="primary"
            inputProps={{ "aria-label": `Site live for ${page.address}` }}
          />
        </Field>
      </div>
    </>
  );
};

const MarketingSessionDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggleError, setToggleError] = useState(null);

  const apiPath = `/api/marketing/sessions/${id}`;

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_PAYVIEW_BASE_URL}${apiPath}/`
        );
        if (!response.ok) {
          throw new Error(
            response.status === 404
              ? "Marketing session not found"
              : "Failed to fetch marketing session"
          );
        }
        setData(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [apiPath]);

  const togglePage = async () => {
    const page = data.property_page;
    setToggleError(null);
    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/marketing/property-pages/${page.id}/`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: !page.is_active }),
        }
      );
      if (!response.ok) throw new Error("Failed to update the property site");
      const updated = await response.json();
      setData((current) => ({ ...current, property_page: updated }));
    } catch (err) {
      setToggleError(err.message);
    }
  };

  const backButton = (
    <div className="deal-details-header">
      <button className="deal-details-back" onClick={() => history.push(LIST_PATH)}>
        &larr; Back to Marketing
      </button>
    </div>
  );

  if (loading || error) {
    return (
      <div className="deal-details-root">
        <div className="deal-details-container">
          {backButton}
          {loading ? (
            <p className="loading">Loading...</p>
          ) : (
            <p className="error">Error: {error}</p>
          )}
        </div>
      </div>
    );
  }

  const {
    session,
    flyers,
    print_orders: printOrders = [],
    property_page: page,
    emails,
    documents,
    timeline,
  } = data;

  return (
    <div className="deal-details-root">
      <div className="deal-details-container">
        {backButton}

        <div className="deal-details-section">
          <h3 className="deal-details-section-title">
            {session.address || "Address not yet provided"}
          </h3>
          <div className="deal-details-grid">
            <Field label="Status">
              <span className={MARKETING_STATUS_CLASS[session.status] || ""}>
                {MARKETING_STATUS_LABELS[session.status] || session.status}
              </span>
            </Field>
            <Field label="Started">{formatDateTime(session.created_at)}</Field>
            <Field label="Last Updated">{formatDateTime(session.updated_at)}</Field>
          </div>
        </div>

        <div className="deal-details-section">
          <h3 className="deal-details-section-title">Flyers</h3>
          <Flyers flyers={flyers} />
        </div>

        <div className="deal-details-section">
          <h3 className="deal-details-section-title">Print Orders</h3>
          <PrintOrders orders={printOrders} />
        </div>

        <div className="deal-details-section">
          <h3 className="deal-details-section-title">Property Site</h3>
          <PropertySite page={page} onToggle={togglePage} toggleError={toggleError} />
        </div>

        <DealHistory
          apiPath={apiPath}
          emails={emails}
          documents={documents}
          timeline={timeline}
        />
      </div>
    </div>
  );
};

export default MarketingSessionDetail;
